import {
    useEffect,
    useState,
} from 'react';

import {DnsRecord} from '../types';

// FNP-1192 — selection, module-scoped for the same reason the keyword and the
// filter are: the toolbar that acts on a selection and the table that makes one
// are not siblings that can share a useState. `SimplePageLayout` renders the
// header as a `pageHeader` prop and the table as its child.
//
// ⚠️ THE FULL RECORD IS KEPT, NOT A SET OF IDS — because there are no ids.
//
// A DNS record has no identifier on the schema; its identity is (name, type)
// within its zone. A delete needs BOTH halves plus the TTL and values to build
// the change, so the whole record is what gets stored. `recordKey` composes the
// pair into the row key `MantineDataTable` needs, and that key is local to the
// browser — it is never sent to the API.

let globalSelection: DnsRecord[] = [];
const selectionListeners: Set<(selected: DnsRecord[]) => void> = new Set();

const notifySelectionChange = (selected: DnsRecord[]) => {
    globalSelection = selected;
    selectionListeners.forEach(listener => listener(selected));
};

// Test-only reset. The store outlives a component tree by design, which means
// it also outlives a test case — without this, a selection made in one test
// leaks into the next.
export const resetDnsRecordsSelection = () => {
    globalSelection = [];
    selectionListeners.clear();
};

export const useDnsRecordsSelection = () => {

    const [selectedRecords, setSelected] = useState<DnsRecord[]>(globalSelection);

    useEffect(() => {
        const listener = (selected: DnsRecord[]) => {
            setSelected(selected);
        };
        selectionListeners.add(listener);
        // A consumer mounting after a selection was made must not start empty.
        setSelected(globalSelection);
        return () => {
            selectionListeners.delete(listener);
        };
    }, []);

    return {
        selectedRecords,
        setSelectedRecords: (selected: DnsRecord[]) => notifySelectionChange(selected),
        clearSelection: () => notifySelectionChange([]),
    };
};
